import { useMemo, useState, type KeyboardEvent as ReactKeyboardEvent, type ReactNode } from 'react';
import { readStored, writeStored } from './types';

export interface NavNode {
  id: string;
  label: string;
  /** Shown as a badge; a count of 0 is rendered, since "none" is an answer. */
  count?: number | undefined;
  hint?: string | undefined;
  /** Something waiting on this node ("Needs review"): a warning mark beside
   *  the label, with these words for screen readers and on hover. */
  flag?: string | undefined;
  children?: NavNode[];
  /** Drawn before the label; purely decorative, the label says what it is. */
  icon?: ReactNode;
}

/**
 * The left-hand tree: pick a store, then narrow to what is wrong with it.
 *
 * A tree rather than a filter bar because it holds the place you are working
 * in — you choose a store once in the morning, and everything else on the
 * screen follows it.
 */
export function Navigator({
  id,
  label,
  nodes,
  selectedId,
  onSelect,
}: {
  id: string;
  label: string;
  nodes: NavNode[];
  selectedId: string;
  onSelect: (id: string) => void;
}) {
  const expandedKey = `poultry.nav.${id}.expanded`;
  const [expanded, setExpanded] = useState<string[]>(() =>
    readStored(expandedKey, nodes.map((n) => n.id)),
  );

  // Flattened to whatever is currently visible, which is what the arrow keys
  // walk — a collapsed node's children are not reachable by keyboard either.
  const visible = useMemo(() => {
    const out: Array<{ node: NavNode; depth: number; hasChildren: boolean }> = [];
    const walk = (list: NavNode[], depth: number) => {
      for (const node of list) {
        const hasChildren = Boolean(node.children?.length);
        out.push({ node, depth, hasChildren });
        if (hasChildren && expanded.includes(node.id)) walk(node.children!, depth + 1);
      }
    };
    walk(nodes, 0);
    return out;
  }, [nodes, expanded]);

  function toggle(nodeId: string) {
    setExpanded((prev) => {
      const next = prev.includes(nodeId) ? prev.filter((x) => x !== nodeId) : [...prev, nodeId];
      writeStored(expandedKey, next);
      return next;
    });
  }

  function onKeyDown(e: ReactKeyboardEvent<HTMLDivElement>) {
    const index = visible.findIndex((v) => v.node.id === selectedId);
    const current = visible[index];
    switch (e.key) {
      case 'ArrowDown':
        onSelect(visible[Math.min(visible.length - 1, index + 1)]?.node.id ?? selectedId);
        break;
      case 'ArrowUp':
        onSelect(visible[Math.max(0, index - 1)]?.node.id ?? selectedId);
        break;
      case 'ArrowRight':
        if (current?.hasChildren && !expanded.includes(current.node.id)) toggle(current.node.id);
        else onSelect(visible[index + 1]?.node.id ?? selectedId);
        break;
      case 'ArrowLeft':
        if (current?.hasChildren && expanded.includes(current.node.id)) toggle(current.node.id);
        else {
          const parent = [...visible.slice(0, index)].reverse().find((v) => v.depth < (current?.depth ?? 0));
          if (parent) onSelect(parent.node.id);
        }
        break;
      case 'Home':
        onSelect(visible[0]?.node.id ?? selectedId);
        break;
      case 'End':
        onSelect(visible[visible.length - 1]?.node.id ?? selectedId);
        break;
      default:
        return;
    }
    e.preventDefault();
  }

  return (
    <nav className="navtree" aria-label={label}>
      <div role="tree" aria-label={label} onKeyDown={onKeyDown}>
        {visible.map(({ node, depth, hasChildren }) => {
          const isOpen = expanded.includes(node.id);
          const selected = node.id === selectedId;
          return (
            <div
              key={node.id}
              role="treeitem"
              aria-selected={selected}
              aria-expanded={hasChildren ? isOpen : undefined}
              aria-level={depth + 1}
              tabIndex={selected ? 0 : -1}
              title={node.hint}
              className={`navtree__node${selected ? ' navtree__node--selected' : ''}`}
              style={{ paddingInlineStart: `${0.5 + depth * 0.9}rem` }}
              onClick={() => onSelect(node.id)}
            >
              {hasChildren ? (
                <button
                  type="button"
                  className="navtree__twisty"
                  aria-label={isOpen ? `Collapse ${node.label}` : `Expand ${node.label}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggle(node.id);
                  }}
                >
                  {isOpen ? '▾' : '▸'}
                </button>
              ) : (
                <span className="navtree__twisty" aria-hidden="true" />
              )}
              {node.icon}
              <span className="navtree__label">{node.label}</span>
              {node.flag && (
                <span className="navtree__flag" title={node.flag}>
                  <span aria-hidden="true">⚠</span>
                  <span className="sr-only">{node.flag}</span>
                </span>
              )}
              {node.count !== undefined && <span className="navtree__count">{node.count}</span>}
            </div>
          );
        })}
      </div>
    </nav>
  );
}
